import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import TrainingApp from "./TrainingApp";
import { defaultTrainingApi } from "./api/trainingApi";
import { isTrainingAdmin } from "../../hooks/usePermissions";
import "./training.css";

const ROLE_KEY = "trn_role";

/**
 * Production entry of the Formation module, mounted by routes/dash.js under /training
 * (gated by the company feature flag `training`).
 *   - agent   = everyone: they follow the path assigned to them, or see an empty state
 *   - manager = ADMIN_TRAINING or Easier Labs staff (they steer, validate and build)
 * A manager who is not linked to an operator has no agent view of their own.
 * Someone who is both gets a Vue agent / Vue manager switch.
 */
const Training = () => {
  const { user } = useSelector((state) => state.user);
  const isManager = isTrainingAdmin(user);
  // sandbox calls and certificate specimens (same rule as the backend `can_test`)
  const canTest = isManager || !!user?.permissions?.some((p) => p === "SUPER_ADMIN");
  const isEasierAdmin = !!user?.is_easier_admin;
  const hasOperator = !!user?.operator_id;
  const isAgent = hasOperator || !isManager;
  const [role, setRoleState] = useState(() => {
    try {
      const saved = window.localStorage.getItem(ROLE_KEY);
      if (saved === "manager" && isManager) return "manager";
      if (saved === "agent" && isAgent) return "agent";
    } catch (e) {
      /* storage blocked: fall through */
    }
    return hasOperator || !isManager ? "agent" : "manager";
  });
  const setRole = (r) => {
    setRoleState(r);
    try {
      window.localStorage.setItem(ROLE_KEY, r);
    } catch (e) {
      /* ignore */
    }
  };
  const env = useMemo(
    () => ({
      basePath: "/training",
      roles: {
        agent: isAgent,
        manager: isManager,
        canTest,
        easierAdmin: isEasierAdmin,
      },
      role: role === "manager" && isManager ? "manager" : "agent",
      setRole,
      dev: false,
    }),
    [isAgent, isManager, canTest, isEasierAdmin, role]
  );
  return (
    <TrainingApp
      api={defaultTrainingApi}
      env={env}
    />
  );
};
export default Training;
